/* GASSISA
========================================================================================
 * Descripción: Modal para validar cliente mediante lector de código de barras
 * Historial de cambios:
 * ---------------------------------------------------------------------------------------
 *    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
 * ---------------------------------------------------------------------------------------
 *      1       | 14/04/2026 | Lic. José Luis Aragón Cervantes  | Creación
 *      2       | 23/09/2026 | LIC. José Luis Aragón C.                       | Deshabilita autocomplete del lector
 * --------------------------------------------------------------------------------------- */

import TextField from "../ui/TextField";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Modal, Box } from "@mui/material";
import { useSesion } from "../../context/System/SesionContext";
import { useVentas } from "../../context/Ventas/VentasContext";
import { useRequest } from "../../context/System/RequestContext";
import { useLayout } from "../../context/System/LayoutContext";
import { apiBuscarClientePorClave } from "../../settings/ApiConfig";
const normalizeClienteCode = (value) => String(value ?? "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[\u0000-\u001F\u007F]/g, "")
  .trim()
  .toUpperCase();

const modalStyle = {
  display: "flex",
  flexDirection: "column",
  position: "fixed",
  top: "20%",
  right: "40%",
  width: "300px",
  bgcolor: "#000000",
  boxShadow: 24,
  p: 3,
  transform: "translateX(300%)",
  transition: "transform 0.3s ease-in-out",
  borderRadius: "8px",
  color: "#FFFFFF",
  border: "2px solid #333",
};

export default function ModalValidarCliente(props) {
  const { openModal, handleClose, handleClienteEncontrado } = props;

  const { parametrosCajaForm } = useSesion();
  const { handleOpenAlert } = useLayout();
  const { GetRequest } = useRequest();

  const [barcode, setBarcode] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const inputRef = useRef(null);

  // Focus input cuando abre
  useEffect(() => {
    if (openModal) {
      setInternalOpen(true);
      setBarcode("");
      setTimeout(() => {
        setModalVisible(true);
        setTimeout(() => inputRef.current?.focus?.(), 100);
      }, 50);
    } else {
      setModalVisible(false);
      setTimeout(() => setInternalOpen(false), 300);
      setBarcode("");
    }
  }, [openModal]);

  const modalVisibleStyle = {
    transform: modalVisible ? "translateX(0)" : "translateX(300%)",
  };

  const handleKeyDown = async (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();

      const claveCliente = normalizeClienteCode(barcode);

      if (!claveCliente.startsWith("C")) {
        handleOpenAlert("Código de cliente debe iniciar con C", "warning");
        setBarcode("");
        return;
      }

      setLoading(true);

      try {
        const response = await GetRequest({
          url: apiBuscarClientePorClave.replace("{clave}", claveCliente),
          loader: "Buscando cliente...",
        });

        setLoading(false);

        if (response.hasError) {
          handleOpenAlert(response.message || "Cliente no encontrado", "error");
          setBarcode("");
          inputRef.current?.focus?.();
          return;
        }

        if (!response.result) {
          handleOpenAlert("Cliente no encontrado", "warning");
          setBarcode("");
          inputRef.current?.focus?.();
          return;
        }

        handleClienteEncontrado(response.result);
        handleClose();
        setBarcode("");

      } catch (error) {
        setLoading(false);
        handleOpenAlert("Error al buscar cliente: " + error.message, "error");
        setBarcode("");
        inputRef.current?.focus?.();
      }
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      handleClose();
      setBarcode("");
      return;
    }

    // Bloquear teclas especiales
    const invalidKeys = [
      "ArrowUp", "ArrowDown", "ArrowRight", "ArrowLeft", "Insert", "Home", "End",
      "PageDown", "PageUp", "ContextMenu", "AltGraph", "Meta", "Tab",
      "Alt", "CapsLock", "F1", "F2", "F3", "F4", "F5", "F6", "F7", "F8", "F9", "F10", "F11"
    ];

    if (invalidKeys.includes(event.key)) {
      event.preventDefault();
      return;
    }

    // Permitir Ctrl+V para pegar
    if (event.ctrlKey && event.key.toLowerCase() === "v") {
      event.preventDefault();
      return;
    }

    if (event.key === "Backspace") {
      event.preventDefault();
      if (!barcode) return;
      setBarcode(normalizeClienteCode(barcode.slice(0, -1)));
      return;
    }

    // Agregar caracteres
    setBarcode(prev => normalizeClienteCode(prev + event.key));
  };

  return (
    <Modal
      open={internalOpen}
      onClose={handleClose}
      aria-labelledby="modal-validar-cliente-title"
      aria-describedby="modal-validar-cliente-description"
      disableEscapeKeyDown={false}
    >
      <Box sx={{ ...modalStyle, ...modalVisibleStyle }}>
        <Box sx={{ mb: 2 }}>
          <h2 className="framedetallesventaLabel" style={{ color: "#FFFFFF" }}>Validar Cliente</h2>
          <p style={{ fontSize: "12px", color: "#AAAAAA" }}>
            Escanee el código de barras del cliente
          </p>
        </Box>

        <input
          ref={inputRef}
          id="txtCodigoBarras"
          className="txtCodigoBarras"
          type="text"
          autoComplete="off"
          name="lector-codigo-barras"
          aria-autocomplete="none"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-lpignore="true"
          data-1p-ignore="true"
          value={barcode}
          onChange={() => {}}
          onKeyDown={handleKeyDown}
          placeholder="Escanee código cliente (C...)"
          disabled={loading}
        />

        {loading && (
          <Box sx={{ mt: 2, textAlign: "center", color: "#AAAAAA" }}>
            Buscando cliente...
          </Box>
        )}

        <Box sx={{ mt: 2, fontSize: "11px", color: "#888888" }}>
          Presione ENTER para buscar • ESC para cerrar
        </Box>
      </Box>
    </Modal>
  );
}
